import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  Clock3,
  RotateCcw,
} from "lucide-react";
import { Mosque } from "@phosphor-icons/react";
import SiteNav from "../components/Layout/SiteNav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getPublicPrayerMonths, getPublicMonthlyPrayerTimes } from "../services/api";
import { loadPublicCalendarContext, clearPublicCalendarContext, savePublicCalendarContext } from "@/lib/publicCalendarContext";

// Format time with prayer-aware AM/PM defaults (Jummah/Duhr smart PM bias)
const formatTimeWithAMPM = (time, prayerName = null) => {
  if (!time) return "-";
  if (/[AaPp][Mm]/.test(time)) return time;

  const [hoursStr, minutesStr] = String(time).split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;

  const padMinutes = minutes.toString().padStart(2, "0");
  let displayHours = hours;
  let period;

  if (prayerName === "fajr" || prayerName === "sunrise") {
    period = "AM";
    displayHours = hours % 12 || 12;
  } else if (prayerName === "duhr" || prayerName === "jummah") {
    // Treat like Dhuhr: only 10:00–11:59 can be AM; everything else PM
    if (hours >= 10 && hours < 12) {
      period = "AM";
      displayHours = hours;
    } else {
      period = "PM";
      displayHours = hours > 12 ? hours - 12 : hours;
    }
  } else if (prayerName === "asr" || prayerName === "maghrib" || prayerName === "isha") {
    period = "PM";
    displayHours = hours > 12 ? hours - 12 : hours;
  } else {
    period = hours >= 12 ? "PM" : "AM";
    displayHours = hours % 12 || 12;
  }

  return `${displayHours}:${padMinutes} ${period}`;
};

const PRAYERS = [
  { key: "fajr", label: "Fajr" },
  { key: "duhr", label: "Dhuhr" },
  { key: "asr", label: "Asr" },
  { key: "maghrib", label: "Maghrib" },
  { key: "isha", label: "Isha" },
];

const monthLabel = (key) => {
  if (!key || !/^[0-9]{4}-[0-9]{2}$/.test(key)) return key || "";
  const [y, m] = key.split("-").map(Number);
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const formatGregorianDate = (yearMonth, day) => {
  if (!yearMonth || !day) return "";
  const [y, m] = yearMonth.split("-").map(Number);
  const dNum = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(dNum)) return `${yearMonth}-${String(day).padStart(2, "0")}`;
  const date = new Date(y, m - 1, dNum); // construct in local tz to avoid UTC shift
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};

export default function PublicMasjidCalendar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMasjid = location.state?.masjid || loadPublicCalendarContext()?.masjid || null;
  const initialMasjidId = location.state?.masjid?.masjidId || location.state?.masjid?.id || location.state?.masjid?.key || loadPublicCalendarContext()?.masjidId || null;
  const widgetToken = searchParams.get("token") || null;

  const [resolvedMasjidId, setResolvedMasjidId] = useState(initialMasjidId);

  const [masjid, setMasjid] = useState(initialMasjid);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [days, setDays] = useState([]);
  const [jummahTimes, setJummahTimes] = useState([]);
  const [khutbahTimes, setKhutbahTimes] = useState([]);
  const [isCompact, setIsCompact] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);
  const tableScrollRef = useRef(null);
  const todayRowRef = useRef(null);

  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const shiftMonthKey = (key, delta) => {
    if (!key || !/^\d{4}-\d{2}$/.test(key)) return null;
    const [y, m] = key.split("-").map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  };

  const stripeClass = (idx) => (idx % 2 === 0 ? "" : "bg-muted/40");
  const stickyHeaderClass = "sticky top-0 z-20 border-b bg-card shadow-sm";

  // Detect compact layout for small screens
  useEffect(() => {
    const updateCompact = () => setIsCompact(window.innerWidth < 768);
    updateCompact();
    window.addEventListener("resize", updateCompact);
    return () => window.removeEventListener("resize", updateCompact);
  }, []);

  // On desktop: auto-scroll to today's row when current month is visible
  useEffect(() => {
    if (isCompact || !tableScrollRef.current || days.length === 0) return;
    if (selectedMonth !== currentMonthKey) return;

    const timer = setTimeout(() => {
      if (todayRowRef.current && tableScrollRef.current) {
        const container = tableScrollRef.current;
        const row = todayRowRef.current;
        const containerTop = container.getBoundingClientRect().top;
        const rowTop = row.getBoundingClientRect().top;
        const offset = rowTop - containerTop - container.clientHeight / 2 + row.clientHeight / 2;
        container.scrollTop += offset;
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [days, isCompact, selectedMonth, currentMonthKey]);

  // Resolve masjid via widget token if needed
  useEffect(() => {
    const resolveMasjid = async () => {
      if (initialMasjidId) {
        setResolvedMasjidId(initialMasjidId);
        return;
      }
      if (!widgetToken) {
        setError("No masjid selected. Please go back and pick a masjid.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const resp = await fetch(`${import.meta.env.VITE_API_URL}/v1/widget-data/${widgetToken}`);
        const data = await resp.json();
        if (!data?.ok || !data?.masjid) {
          throw new Error(data?.error || "Unable to resolve masjid from widget token");
        }
        const id = data.masjid.masjidId || data.masjid.id || data.masjid.key || data.masjid.masjid_id;
        if (!id) {
          throw new Error("Widget token resolved masjid without id");
        }
        setMasjid(data.masjid);
        setResolvedMasjidId(id);
        savePublicCalendarContext({ masjidId: id, masjid: data.masjid });
      } catch (err) {
        console.error("Public calendar: resolve masjid from widget token failed", err);
        setError("Unable to load masjid for this widget link.");
      } finally {
        setLoading(false);
      }
    };

    resolveMasjid();
  }, [initialMasjidId, widgetToken]);

  // Load available months and masjid meta
  useEffect(() => {
    const loadMonths = async () => {
      if (!resolvedMasjidId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicPrayerMonths(resolvedMasjidId);
        setMasjid((prev) => {
          const incoming = data.masjid || null;
          if (!prev) return incoming;
          if (incoming && (!prev.title && incoming.title)) return incoming;
          return prev;
        });
        const months = Array.isArray(data.months) ? data.months.slice().sort() : [];
        setAvailableMonths(months);
        const initial = months.includes(currentMonthKey)
          ? currentMonthKey
          : months[months.length - 1] || null;
        setSelectedMonth(initial);
        savePublicCalendarContext({ masjidId: resolvedMasjidId, masjid: data.masjid || masjid || null });
      } catch (err) {
        console.error("Public calendar: load months error", err);
        setError("Unable to load available months for this masjid.");
      } finally {
        setLoading(false);
      }
    };

    loadMonths();
  }, [resolvedMasjidId, currentMonthKey, masjid]);

  // Load month data when selection changes
  useEffect(() => {
    const loadMonth = async () => {
      if (!resolvedMasjidId || !selectedMonth) return;
      setMonthLoading(true);
      setError(null);
      try {
        const data = await getPublicMonthlyPrayerTimes(resolvedMasjidId, selectedMonth);
        const masjidSource = data.masjid?.source || "unknown";
        const masjidalId = data.masjid?.masjidal_id || data.masjid?.masjidalId || data.masjid?.masjidalID;

        setMasjid((prev) => {
          const incoming = data.masjid || null;
          if (!prev) return incoming;
          if (incoming && (!prev.title && incoming.title)) return incoming;
          return prev;
        });
        const monthDays = Array.isArray(data.days) ? data.days.slice() : [];
        monthDays.sort((a, b) => (a.day || "").localeCompare(b.day || ""));
        setDays(monthDays);
        if (monthDays.length > 0) {
          const today = new Date();
          const isoToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
          const match = monthDays.find((d) => `${selectedMonth}-${d.day}` === isoToday);
          setSelectedDayId((match && match.day) || monthDays[0].day);
        } else {
          setSelectedDayId(null);
        }
        const normalizeTime = (t) => {
          if (!t) return null;
          // Handle times with AM/PM suffix (e.g., "12:30 PM")
          const timeStr = String(t).trim();
          const ampmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
          if (!ampmMatch) return null;
          let h = parseInt(ampmMatch[1], 10);
          const m = parseInt(ampmMatch[2], 10);
          const suffix = ampmMatch[3];
          if (Number.isNaN(h) || Number.isNaN(m)) return null;
          // Convert to 24-hour format for sorting
          if (suffix) {
            const isPM = suffix.toLowerCase() === 'pm';
            if (isPM && h !== 12) h += 12;
            else if (!isPM && h === 12) h = 0;
          } else {
            // No AM/PM: Jummah behaves like Duhr: 10-11 is AM, otherwise PM
            if (!(h >= 10 && h < 12)) {
              h = ((h % 12) + 12);
            }
          }
          return { raw: t, minutes: h * 60 + m };
        };
        const sortedJummah = (Array.isArray(data.jummahTimes) ? data.jummahTimes : [])
          .map(normalizeTime)
          .filter(Boolean)
          .sort((a, b) => a.minutes - b.minutes)
          .map((entry) => entry.raw);
        setJummahTimes(sortedJummah);
        
        // Process khutbah times (similar to jummah)
        const sortedKhutbah = (Array.isArray(data.khutbahTimes) ? data.khutbahTimes : [])
          .map(normalizeTime)
          .filter(Boolean)
          .sort((a, b) => a.minutes - b.minutes)
          .map((entry) => entry.raw);
        setKhutbahTimes(sortedKhutbah);

      } catch (err) {
        console.error("Public calendar: load month error", err);
        setError("Unable to load prayer times for this month.");
        setDays([]);
        setJummahTimes([]);
        setKhutbahTimes([]);
      } finally {
        setMonthLoading(false);
      }
    };

    loadMonth();
  }, [resolvedMasjidId, selectedMonth]);

  const onPrevMonth = () => {
    if (!selectedMonth) return;
    const prev = shiftMonthKey(selectedMonth, -1);
    if (!prev) return;
    setAvailableMonths((prevList) => {
      if (prevList.includes(prev)) return prevList;
      return [...prevList, prev].sort();
    });
    setSelectedMonth(prev);
  };

  const onNextMonth = () => {
    if (!selectedMonth) return;
    const next = shiftMonthKey(selectedMonth, 1);
    if (!next) return;
    setAvailableMonths((prevList) => {
      if (prevList.includes(next)) return prevList;
      return [...prevList, next].sort();
    });
    setSelectedMonth(next);
  };

  const onResetMonth = () => {
    if (!currentMonthKey) return;
    setAvailableMonths((prevList) => {
      if (prevList.includes(currentMonthKey)) return prevList;
      return [...prevList, currentMonthKey].sort();
    });
    setSelectedMonth(currentMonthKey);
    setSelectedDayId(null);
  };

  useEffect(() => () => clearPublicCalendarContext(), []);

  const isToday = (yearMonth, day) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return `${yearMonth}-${String(day).padStart(2, "0")}` === todayStr;
  };

  // If selected day disappears after data change, reset to first
  useEffect(() => {
    if (!days || days.length === 0) {
      setSelectedDayId(null);
      return;
    }
    if (!selectedDayId || !days.find((d) => d.day === selectedDayId)) {
      setSelectedDayId(days[0].day);
    }
  }, [days, selectedDayId]);

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-none px-4 sm:px-6 lg:px-12 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Badge variant="outline" className="gap-1">
              <CalendarIcon className="h-4 w-4" />
              Monthly Prayer Calendar
            </Badge>
          </div>
          {masjid?.timezone && (
            <div className="text-xs text-muted-foreground">Timezone: {masjid.timezone}</div>
          )}
        </div>

        <Card className="mb-6 border-2 shadow-lg">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-primary/10 p-3 text-primary">
                <Mosque size={24} weight="fill" className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">
                  {masjid?.title || masjid?.name || masjid?.mosqueName || "Masjid"}
                </CardTitle>
                <CardDescription className="flex items-start gap-2 text-base">
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {[masjid?.address, masjid?.city, masjid?.state, masjid?.zip]
                      .filter(Boolean)
                      .join(", ") || "Location unavailable"}
                  </span>
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={onResetMonth}
                disabled={!selectedMonth}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={onPrevMonth}
                disabled={!selectedMonth}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="rounded-full bg-muted px-4 py-2 text-sm font-semibold">
                {selectedMonth ? monthLabel(selectedMonth) : "No month selected"}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={onNextMonth}
                disabled={!selectedMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading calendar...</span>
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : availableMonths.length === 0 ? (
              <Alert>
                <AlertDescription>
                  This masjid has not published monthly prayer times yet. Check back soon.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {monthLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading {monthLabel(selectedMonth)}...</span>
                  </div>
                ) : days.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      No entries found for {monthLabel(selectedMonth)}.
                    </AlertDescription>
                  </Alert>
                ) : isCompact ? (
                  <div className="space-y-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm font-semibold text-muted-foreground">Select a day</div>
                      <select
                        className="w-full rounded border border-input bg-background px-3 py-2 text-sm text-foreground sm:w-auto"
                        value={selectedDayId || ''}
                        onChange={(e) => setSelectedDayId(e.target.value)}
                      >
                        {days.map((d) => (
                          <option key={d.day} value={d.day}>
                            {formatGregorianDate(selectedMonth, d.day)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(() => {
                      const current = days.find((d) => d.day === selectedDayId) || days[0];
                      if (!current) return null;
                      const jummahForDay = Object.entries(current.jummah || {}).filter(([_, v]) => v && v.trim());
                      const khutbahForDay = current.khutbah || {};
                      return (
                        <div className="overflow-hidden rounded-lg border">
                          <div className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-muted-foreground">
                            <span>{current.islamicDate || '-'}</span>
                          </div>
                          <Table className="min-w-full">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[45%]">Prayer</TableHead>
                                <TableHead className="w-[27%] text-right">Adhan</TableHead>
                                <TableHead className="w-[28%] text-right">Iqama</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium capitalize">Fajr</TableCell>
                                <TableCell className="text-center whitespace-nowrap">{formatTimeWithAMPM(current.fajr?.adhan, "fajr")}</TableCell>
                                <TableCell className="font-semibold text-center whitespace-nowrap">{formatTimeWithAMPM(current.fajr?.iqama, "fajr")}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium">Sunrise</TableCell>
                                <TableCell className="text-center whitespace-nowrap">{formatTimeWithAMPM(current.sunrise, "sunrise")}</TableCell>
                                <TableCell className="font-semibold text-center whitespace-nowrap">—</TableCell>
                              </TableRow>
                              {["duhr","asr","maghrib","isha"].map((key) => (
                                <TableRow key={key}>
                                  <TableCell className="font-medium capitalize">{key === "duhr" ? "Dhuhr" : key}</TableCell>
                                  <TableCell className="text-center whitespace-nowrap">{formatTimeWithAMPM(current[key]?.adhan, key)}</TableCell>
                                  <TableCell className="font-semibold text-center whitespace-nowrap">{formatTimeWithAMPM(current[key]?.iqama, key)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div ref={tableScrollRef} className="overflow-auto max-h-[60vh] rounded-lg border">
                    <table className="w-full table-fixed caption-bottom border-separate border-spacing-0 text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className={`${stickyHeaderClass} w-[140px] min-w-0`}>Gregorian Date</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[120px] min-w-0`}>Hijri Date</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Fajr Adhan</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Fajr Iqama</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Sunrise</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Dhuhr Adhan</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Dhuhr Iqama</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Asr Adhan</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Asr Iqama</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Maghrib Adhan</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Maghrib Iqama</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Isha Adhan</TableHead>
                          <TableHead className={`${stickyHeaderClass} w-[90px] min-w-0 text-center`}>Isha Iqama</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {days.map((day) => {
                          const todayRow = isToday(selectedMonth, day.day);

                          return (
                            <TableRow key={day.day} ref={todayRow ? todayRowRef : null} className={todayRow ? "bg-primary/5" : undefined}>
                              <TableCell className={`font-medium min-w-0 whitespace-normal break-words leading-tight ${stripeClass(0)}`}>
                                <div className="flex items-center gap-2">
                                  {todayRow && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" aria-hidden="true" />}
                                  <div className={`max-h-[3rem] overflow-hidden ${todayRow ? "font-semibold" : ""}`}>
                                    {formatGregorianDate(selectedMonth, day.day)}
                                  </div>
                                </div>
                                {day.customOverride && (
                                  <Badge variant="secondary" className="mt-1">Custom</Badge>
                                )}
                              </TableCell>
                              <TableCell className={`min-w-0 whitespace-normal break-words leading-tight ${stripeClass(1)}`}>
                                <div className="max-h-[3rem] overflow-hidden">{day.islamicDate || "-"}</div>
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(2)}`}
                              >
                                {formatTimeWithAMPM(day.fajr?.adhan, "fajr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 font-semibold text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(3)}`}
                              >
                                {formatTimeWithAMPM(day.fajr?.iqama, "fajr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(4)}`}
                              >
                                {formatTimeWithAMPM(day.sunrise, "sunrise")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(5)}`}
                              >
                                {formatTimeWithAMPM(day.duhr?.adhan, "duhr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 font-semibold text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(6)}`}
                              >
                                {formatTimeWithAMPM(day.duhr?.iqama, "duhr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(7)}`}
                              >
                                {formatTimeWithAMPM(day.asr?.adhan, "asr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 font-semibold text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(8)}`}
                              >
                                {formatTimeWithAMPM(day.asr?.iqama, "asr")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(9)}`}
                              >
                                {formatTimeWithAMPM(day.maghrib?.adhan, "maghrib")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 font-semibold text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(10)}`}
                              >
                                {formatTimeWithAMPM(day.maghrib?.iqama, "maghrib")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(11)}`}
                              >
                                {formatTimeWithAMPM(day.isha?.adhan, "isha")}
                              </TableCell>
                              <TableCell
                                className={`min-w-0 font-semibold text-center whitespace-normal break-words leading-tight max-h-[2.5rem] overflow-hidden ${stripeClass(12)}`}
                              >
                                {formatTimeWithAMPM(day.isha?.iqama, "isha")}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </table>
                  </div>
                )}

{/* Jummah Times Table */}
                <div className="overflow-hidden rounded-lg border">
                  <div className="flex items-center justify-center gap-2 bg-muted/40 px-4 py-3 border-b">
                    <Clock3 className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Jummah Times</span>
                  </div>
                  {jummahTimes.length > 0 ? (
                    khutbahTimes.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-center">Khutbah</TableHead>
                            <TableHead className="text-center">Prayer</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            const maxRows = Math.max(jummahTimes.length, khutbahTimes.length);
                            return Array.from({ length: maxRows }, (_, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="text-center">
                                  {khutbahTimes[idx] ? formatTimeWithAMPM(khutbahTimes[idx], "jummah") : "—"}
                                </TableCell>
                                <TableCell className="text-center font-semibold">
                                  {jummahTimes[idx] ? formatTimeWithAMPM(jummahTimes[idx], "jummah") : "—"}
                                </TableCell>
                              </TableRow>
                            ));
                          })()}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="flex flex-wrap justify-center gap-4 px-4 py-4">
                        {jummahTimes.map((time, idx) => (
                          <div key={idx} className="text-center font-semibold">
                            {formatTimeWithAMPM(time, "jummah")}
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Jummah times not available for this month.
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
